import { useState, Fragment } from "react";
import { useGetLogs, type ActivityLog } from "../../api/routes/logs";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react";
import moment from "moment";

const categoryColors: Record<string, string> = {
    audit: "bg-blue-100 text-blue-800",
    mqtt: "bg-green-100 text-green-800",
    device: "bg-purple-100 text-purple-800",
    auth: "bg-amber-100 text-amber-800",
};

const CATEGORIES = ["all", "audit", "mqtt", "device", "auth"] as const;

interface RecordLogsProps {
    resourceType: string;
    resourceId: string;
    title?: string;
}

const RecordLogs = ({ resourceType, resourceId, title = "Activity Logs" }: RecordLogsProps) => {
    const [category, setCategory] = useState<string | undefined>(undefined);
    const [page, setPage] = useState(1);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    const { data, isLoading } = useGetLogs({
        resourceType,
        resourceId,
        category: category || undefined,
        page,
        pageSize: 25,
    });

    const toggleRow = (id: string) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const pagination = data?.pagination;

    return (
        <div className="rounded-lg border bg-card mb-6">
            <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    {title}
                </h3>
                <div className="flex gap-1">
                    {CATEGORIES.map((cat) => (
                        <Button
                            key={cat}
                            variant={(category || "all") === cat ? "default" : "ghost"}
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => {
                                setCategory(cat === "all" ? undefined : cat);
                                setPage(1);
                            }}
                        >
                            {cat === "all" ? "All" : cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </Button>
                    ))}
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b bg-muted/50">
                            <th className="px-2 py-3 w-[30px]"></th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Timestamp</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Category</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Action</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actor</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-8 text-center text-sm text-muted-foreground">
                                    Loading...
                                </td>
                            </tr>
                        ) : !data?.logs?.length ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-8 text-center text-sm text-muted-foreground">
                                    No activity found.
                                </td>
                            </tr>
                        ) : (
                            data.logs.map((log: ActivityLog) => {
                                const hasDetails = log.beforeData || log.afterData || log.metadata;
                                const isExpanded = expandedRows.has(log.id);

                                return (
                                    <Fragment key={log.id}>
                                        <tr
                                            className={`border-b last:border-0 hover:bg-muted/30 transition-colors ${hasDetails ? "cursor-pointer" : ""}`}
                                            onClick={() => hasDetails && toggleRow(log.id)}
                                        >
                                            <td className="px-2 py-3">
                                                {hasDetails && (
                                                    isExpanded
                                                        ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                                        : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                )}
                                            </td>
                                            <td className="px-6 py-3 text-sm font-mono text-xs whitespace-nowrap">
                                                {moment(log.createdAt).format("YYYY-MM-DD h:mm:ss A")}
                                            </td>
                                            <td className="px-6 py-3">
                                                <Badge className={categoryColors[log.category] || ""} variant="outline">
                                                    {log.category}
                                                </Badge>
                                            </td>
                                            <td className="px-6 py-3 text-sm">{log.action}</td>
                                            <td className="px-6 py-3 text-sm">{log.actorName || "-"}</td>
                                            <td className="px-6 py-3 text-sm">{log.message}</td>
                                        </tr>
                                        {isExpanded && (
                                            <tr>
                                                <td colSpan={6} className="bg-muted/20 px-8 py-4">
                                                    {(log.beforeData || log.afterData) && (
                                                        <div className="grid grid-cols-2 gap-4 text-xs">
                                                            {log.beforeData && (
                                                                <div>
                                                                    <p className="font-medium text-muted-foreground mb-1">Before</p>
                                                                    <div className="bg-red-50 rounded p-2 space-y-0.5">
                                                                        {Object.entries(log.beforeData).map(([key, value]) => (
                                                                            <div key={key}>
                                                                                <span className="text-muted-foreground">{key}:</span>{" "}
                                                                                <span className="font-mono">{typeof value === "object" ? JSON.stringify(value) : String(value)}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {log.afterData && (
                                                                <div>
                                                                    <p className="font-medium text-muted-foreground mb-1">After</p>
                                                                    <div className="bg-green-50 rounded p-2 space-y-0.5">
                                                                        {Object.entries(log.afterData).map(([key, value]) => (
                                                                            <div key={key}>
                                                                                <span className="text-muted-foreground">{key}:</span>{" "}
                                                                                <span className="font-mono">{typeof value === "object" ? JSON.stringify(value) : String(value)}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {log.metadata && (
                                                        <div className="mt-3 text-xs">
                                                            <p className="font-medium text-muted-foreground mb-1">Metadata</p>
                                                            <pre className="bg-muted rounded p-2 overflow-x-auto">
                                                                {JSON.stringify(log.metadata, null, 2)}
                                                            </pre>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-3 border-t">
                    <p className="text-xs text-muted-foreground">
                        {((pagination.page - 1) * pagination.pageSize) + 1}-{Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total}
                    </p>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7"
                            disabled={pagination.page <= 1}
                            onClick={() => setPage((p) => p - 1)}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-xs">
                            {pagination.page} / {pagination.totalPages}
                        </span>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7"
                            disabled={pagination.page >= pagination.totalPages}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecordLogs;
